import ClearIcon from '@mui/icons-material/Clear';
import CloseIcon from '@mui/icons-material/Close';
import DeleteForeverIcon from '@mui/icons-material/DeleteForever';
import EditIcon from '@mui/icons-material/Edit';
import SaveIcon from '@mui/icons-material/Save';
import Autocomplete from '@mui/material/Autocomplete';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Chip from '@mui/material/Chip';
import CircularProgress from '@mui/material/CircularProgress';
import IconButton from '@mui/material/IconButton';
import TableCell from '@mui/material/TableCell';
import TableRow from '@mui/material/TableRow';
import TextField from '@mui/material/TextField';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import React, { useContext, useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { toast } from 'react-toastify';

import deleteBrandGroup from '../../../../../api/oc_product/brandGroup/delete/deleteBrandGroup';
import getBrandGroupBrands from '../../../../../api/oc_product/brandGroup/get/getBrandGroupBrands';
import patchAddRemoveBrands from '../../../../../api/oc_product/brandGroup/patch/patchAddRemoveBrands';
import Modal from '../../../../../components/Modal';
import { BrandGroupingContext } from '../BrandGroupingContext';

const MAX_VISIBLE_BRANDS = 3;

function BrandGroupCard({ group }) {
  const siteId = useSelector((state) => state.user.loginResponse?.selectedSite?.SiteId);
  const { brands, setFetchData, setReFetchLogs } = useContext(BrandGroupingContext);

  const [editGroup, setEditGroup] = React.useState(false);
  const [originalBrands, setOriginalBrands] = React.useState([]);
  const [selectedBrands, setSelectedBrands] = React.useState([]);
  const [openModal, setOpenModal] = React.useState(false);
  const [openSaveConfirmModal, setOpenSaveConfirmModal] = React.useState(false);
  const [loading, setLoading] = React.useState(false);
  const [isUpdating, setIsUpdating] = React.useState(false);
  const [status, setStatus] = React.useState(group.status || 'Active');
  const [isBrandsExpanded, setIsBrandsExpanded] = React.useState(false);

  useEffect(() => {
    if (brands && group.BrandGroupId) {
      getBrandGroupBrands(siteId, group.BrandGroupId)
        .then((res) => {
          const filteredBrands = brands.filter((brand) => res.data.some((selectedBrand) => selectedBrand.BrandId === brand.BrandId));
          setSelectedBrands(filteredBrands);
          setOriginalBrands(filteredBrands);
        })
        .catch((err) => console.error(err));
    }
  }, [group.BrandGroupId, brands, siteId]);

  const handleModalClose = () => setOpenModal(false);
  const handleSaveConfirmModalClose = () => setOpenSaveConfirmModal(false);

  const handleDeleteBrandGroup = async () => {
    try {
      setLoading(true);
      await deleteBrandGroup(siteId, group.BrandGroupId);
      setFetchData(true);
      setReFetchLogs(true);
      toast.success(`Brand group ${group.BrandGroupName} successfully deleted`);
    } catch (error) {
      console.error(error);
      toast.error('Failed to delete brand group');
    } finally {
      setOpenModal(false);
      setLoading(false);
    }
  };

  const handleUpdate = async () => {
    const removed = originalBrands.filter((item) => !selectedBrands.some((sel) => sel.BrandId === item.BrandId));
    const inserted = selectedBrands.filter((item) => !originalBrands.some((orig) => orig.BrandId === item.BrandId));

    if (removed.length === 0 && inserted.length === 0) {
      toast.info('No changes to save.');
      setEditGroup(false);
      setOpenSaveConfirmModal(false);
      return;
    }

    const body = {
      brandGroupInfo: {
        removed: removed.map((brand) => brand.BrandId),
        inserted: inserted.map((brand) => brand.BrandId),
      },
    };

    try {
      setIsUpdating(true);
      await patchAddRemoveBrands(siteId, group.BrandGroupId, body);
      setFetchData(true);
      setReFetchLogs(true);
      toast.success(`Brand group ${group.BrandGroupName} successfully updated`);
      setOriginalBrands(selectedBrands);
    } catch (error) {
      console.error(error);
      toast.error('Failed to update brand group');
    } finally {
      setIsUpdating(false);
      setEditGroup(false);
      setOpenSaveConfirmModal(false);
    }
  };

  const handleCancel = () => {
    setSelectedBrands(originalBrands);
    setEditGroup(false);
  };

  const handleSaveClick = () => {
    const removed = originalBrands.filter((item) => !selectedBrands.some((sel) => sel.BrandId === item.BrandId));
    const inserted = selectedBrands.filter((item) => !originalBrands.some((orig) => orig.BrandId === item.BrandId));

    if (removed.length === 0 && inserted.length === 0) {
      toast.info('No changes to save.');
      setEditGroup(false);
      return;
    }

    setOpenSaveConfirmModal(true);
  };

  const getChangesSummary = () => {
    const removed = originalBrands.filter((item) => !selectedBrands.some((sel) => sel.BrandId === item.BrandId));
    const inserted = selectedBrands.filter((item) => !originalBrands.some((orig) => orig.BrandId === item.BrandId));

    const summary = [];

    if (removed.length > 0) {
      summary.push(`Removing ${removed.length} brand(s): ${removed.map((brand) => brand.Name).join(', ')}`);
    }

    if (inserted.length > 0) {
      summary.push(`Adding ${inserted.length} brand(s): ${inserted.map((brand) => brand.Name).join(', ')}`);
    }

    return summary;
  };

  const renderBrandsList = () => {
    if (editGroup) {
      return (
        <Box sx={{ width: '100%' }}>
          <Autocomplete
            multiple
            id={`brands-${group.BrandGroupId}`}
            size="small"
            value={selectedBrands}
            onChange={(e, value) => setSelectedBrands(value)}
            options={brands || []}
            getOptionLabel={(brand) => (brand ? brand.Name : '')}
            limitTags={2}
            renderInput={(params) => (
              <TextField
                {...params}
                label="Select Brands"
                variant="outlined"
                fullWidth
              />
            )}
          />
          <Box sx={{
            display: 'flex', gap: 1, mt: 1, justifyContent: 'flex-end',
          }}
          >
            <Button
              variant="outlined"
              size="small"
              onClick={handleCancel}
              disabled={isUpdating}
              startIcon={<ClearIcon />}
            >
              Cancel
            </Button>
            <Button
              variant="contained"
              size="small"
              onClick={handleSaveClick}
              disabled={isUpdating}
              startIcon={isUpdating ? <CircularProgress size={16} /> : <SaveIcon />}
            >
              {isUpdating ? 'Saving...' : 'Save'}
            </Button>
          </Box>
        </Box>
      );
    }

    const totalBrands = selectedBrands.length;

    if (isBrandsExpanded) {
      return (
        <Box sx={{
          position: 'absolute',
          zIndex: 10,
          backgroundColor: 'white',
          border: '1px solid #ccc',
          p: 1,
          boxShadow: 3,
          maxWidth: '400px',
          mt: -1,
        }}
        >
          <IconButton
            onClick={() => setIsBrandsExpanded(false)}
            sx={{
              position: 'absolute',
              right: 0,
              top: 0,
              p: 1,
              color: '#495057',
            }}
          >
            <CloseIcon />
          </IconButton>

          <Box sx={{
            display: 'flex', flexWrap: 'wrap', gap: 0.5, pr: 3, pb: 1,
          }}
          >
            {selectedBrands.map((brand) => (
              <Chip key={brand.BrandId} label={brand.Name} size="small" />
            ))}
          </Box>
        </Box>
      );
    }

    const visibleBrands = selectedBrands.slice(0, MAX_VISIBLE_BRANDS);
    const remainingCount = totalBrands - MAX_VISIBLE_BRANDS;

    return (
      <Box sx={{
        display: 'flex', flexWrap: 'wrap', gap: 0.5, alignItems: 'center',
      }}
      >
        {visibleBrands.map((brand) => (
          <Chip key={brand.BrandId} label={brand.Name} size="small" />
        ))}
        {remainingCount > 0 && (
          <Chip
            label={`+${remainingCount} more`}
            size="small"
            variant="outlined"
            onClick={() => setIsBrandsExpanded(true)}
            sx={{ cursor: 'pointer', '&:hover': { backgroundColor: '#f0f0f0' } }}
          />
        )}
        {totalBrands === 0 && group.Name && <Typography variant="body2">{group.Name}</Typography>}
      </Box>
    );
  };

  return (
    <>
      {!editGroup && (
        <TableRow hover sx={{ minHeight: '40px' }}>
          <TableCell>{group.BrandGroupId}</TableCell>
          <TableCell>{group.BrandGroupName}</TableCell>
          <TableCell sx={{ minWidth: '300px', position: 'relative' }}>
            {renderBrandsList()}
          </TableCell>
          <TableCell sx={{ minWidth: '100px' }}>
            <Tooltip title="Edit Group">
              <IconButton
                size="small"
                onClick={() => setEditGroup(true)}
                sx={{
                  '&:hover': { color: 'green', backgroundColor: 'rgba(0, 255, 0, 0.1)' },
                }}
              >
                <EditIcon />
              </IconButton>
            </Tooltip>
          </TableCell>
          <TableCell>
            <Tooltip title="Delete Group">
              <IconButton
                size="small"
                onClick={() => setOpenModal(true)}
                sx={{
                  '&:hover': { color: 'red', backgroundColor: 'rgba(255, 0, 0, 0.1)' },
                }}
              >
                <DeleteForeverIcon />
              </IconButton>
            </Tooltip>
          </TableCell>
        </TableRow>
      )}

      {editGroup && (
        <TableRow sx={{ backgroundColor: '#f8f9fa' }}>
          <TableCell colSpan={5} sx={{ p: 2 }}>
            <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 2 }}>
              {/* Group Info */}
              <Box sx={{ minWidth: '200px' }}>
                <Typography variant="subtitle2" sx={{ fontWeight: 'bold' }}>
                  {group.BrandGroupName}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  ID:
                  {' '}
                  {group.BrandGroupId}
                </Typography>
              </Box>

              {/* Brand Selection */}
              <Box sx={{ flex: 1 }}>
                {renderBrandsList()}
              </Box>
            </Box>
          </TableCell>
        </TableRow>
      )}

      <Modal
        title={`Delete ${group.BrandGroupName} brand group`}
        open={openModal}
        onClose={handleModalClose}
        buttonLabel="Confirm"
        buttonOnClick={handleDeleteBrandGroup}
      >
        {loading ? (
          <div className="flex items-center justify-center h-16">
            <CircularProgress color="primary" />
          </div>
        ) : (
          <Typography>{`Are you sure you want to delete the ${group.BrandGroupName} brand group`}</Typography>
        )}
      </Modal>

      <Modal
        title={`Save changes to ${group.BrandGroupName}`}
        open={openSaveConfirmModal}
        onClose={handleSaveConfirmModalClose}
        buttonLabel="Yes"
        buttonOnClick={handleUpdate}
        showCancelButton
        cancelButtonLabel="Cancel"
        cancelButtonOnClick={handleSaveConfirmModalClose}
      >
        <Box sx={{ mb: 2 }}>
          <Typography variant="body1" sx={{ mb: 2 }}>
            Are you sure you want to save the following changes?
          </Typography>
          {getChangesSummary().map((change, index) => (
            <Typography key={index} variant="body2" sx={{ mb: 1, color: 'text.secondary' }}>
              •
              {' '}
              {change}
            </Typography>
          ))}
        </Box>
        {isUpdating && (
          <Box sx={{ display: 'flex', justifyContent: 'center', mt: 2 }}>
            <CircularProgress size={24} />
          </Box>
        )}
      </Modal>
    </>
  );
}

export default BrandGroupCard;
